import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getCarResponsiblesHistory } from '../../../api/carsApi';

import { ResponsibleHistoryItem } from '../../../interfaces/ResponsibleCollaboratorHistory';

import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';

import '../status-history/StatusHistoryModal.css';

interface ResponsiblesHistoryModalProps {
  isOpen: boolean;
  carId: string;
  onCancel: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
}

const ResponsiblesHistoryModal: React.FC<ResponsiblesHistoryModalProps> = ({
  isOpen,
  carId,
  onCancel,
  sidebarRef
}) => {
  const [responsiblesHistory, setResponsiblesHistory] = useState<ResponsibleHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const limit = 10;
  const modalRef = useRef<HTMLDivElement>(null);

  const fetchResponsiblesHistory = useCallback(async (page: number) => {
    try {
      setLoading(true);
      setError('');
      const response = await getCarResponsiblesHistory(carId, page, limit);
      setResponsiblesHistory(response.history);
      setTotalPages(Math.ceil(response.total / limit));
      setLoading(false);
    } catch (err) {
      setError('Erro ao carregar o histórico de responsáveis');
      setLoading(false);
    }
  }, [carId, limit]);

  useEffect(() => {
    if (isOpen) {
      fetchResponsiblesHistory(currentPage);
    }
  }, [isOpen, currentPage, fetchResponsiblesHistory]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef]);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  if (!isOpen) {
    return null;
  }

  function formatHoursAndSeconds(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  }

  return (
    <div className="add-car-modal-overlay modal-overlay--empilhado">
      <div className="parts-history-modal" ref={modalRef}>
        <div className="parts-history-title">Histórico de Responsáveis</div>

        <div className="parts-history-grid">
          <div className="parts-history-row parts-history-row--4 parts-history-header">
            <div>Nome do Responsável</div>
            <div>Tempo no Status</div>
            <div>Data de Atribuição</div>
            <div>Válido até</div>
          </div>

          {loading && <div className="parts-history-empty">Carregando histórico...</div>}
          {!loading && error && <div className="parts-history-empty">{error}</div>}
          {!loading && !error && responsiblesHistory.length === 0 && (
            <div className="parts-history-empty">Sem histórico de responsáveis para este veículo.</div>
          )}

          {!loading && !error && responsiblesHistory
            .slice()
            .reverse()
            .map((responsible, index) => (
              <div className="parts-history-row parts-history-row--4" key={`${responsible.assignedAt}-${responsible.name}-${index}`}>
                <div>{responsible.name}</div>
                <div>
                  {responsible.secondsResponsible !== null
                    ? formatHoursAndSeconds(responsible.secondsResponsible)
                    : 'N/A'}
                </div>
                <div>{new Date(responsible.assignedAt).toLocaleString()}</div>
                <div>{responsible.validTo ? new Date(responsible.validTo).toLocaleString() : 'Atual'}</div>
              </div>
            ))}
        </div>

        <div className="parts-history-pagination">
          <div
            className={`Frame1000003950 ${currentPage === 1 ? 'disabled' : ''}`}
            onClick={currentPage > 1 ? handlePreviousPage : undefined}
            style={{
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              pointerEvents: currentPage === 1 ? 'none' : 'auto',
            }}
            aria-disabled={currentPage === 1}
          >
            <img
              className="imgIcon"
              src={currentPage === 1 ? ArrowLeftGray : ArrowLeftOrange}
              alt="Seta para Esquerda"
              width="24"
              height="24"
            />
            <div
              className="Anterior"
              style={{ color: currentPage === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
            >
              Anterior
            </div>
          </div>

          <div className="Lta2456">Página {currentPage} de {totalPages}</div>

          <div
            className={`Frame1000003949 ${currentPage >= totalPages ? 'disabled' : ''}`}
            onClick={currentPage < totalPages ? handleNextPage : undefined}
            style={{
              cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
            }}
          >
            <div
              className="PrXima"
              style={{ color: currentPage >= totalPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
            >
              Próxima
            </div>
            <img
              className="imgIcon"
              src={currentPage >= totalPages ? ArrowRightGray : ArrowRightOrange}
              alt="Seta para Direita"
              width="24"
              height="24"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponsiblesHistoryModal;
